import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  FaChevronRight,
  FaChevronDown,
  FaPlus,
  FaTrash,
  FaPencilAlt,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
} from "react-icons/fa";
import { VscFolder, VscFolderOpened, VscFile } from "react-icons/vsc";
import ContextMenu from "../common/ContextMenu";

const PromptNode = ({
  node,
  depth,
  onContextMenu,
  initiallyExpanded,
  selectedPath,
  onSelect,
  onAddFile,
  isAddingFile,
  onCancelAddFile,
  isRenamingFile,
  onRenameFile,
  onCancelRenameFile,
  existingPaths,
  onMovePrompt,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyExpanded);
  const [newFileName, setNewFileName] = useState("");
  const newFileInputRef = useRef(null);
  const isFolder = depth < 2;
  const isSelected = selectedPath === node.path;

  useEffect(() => {
    if (selectedPath && selectedPath.startsWith(node.path)) {
      setIsOpen(true);
    }
  }, [selectedPath, node.path]);

  useEffect(() => {
    if ((isAddingFile || isRenamingFile) && newFileInputRef.current) {
      newFileInputRef.current.focus();
      if (isRenamingFile) {
        newFileInputRef.current.select();
      }
    }
  }, [isAddingFile, isRenamingFile]);

  useEffect(() => {
    if (isRenamingFile === node.path) {
      setNewFileName(node.name);
    }
  }, [isRenamingFile, node.name, node.path]);

  useEffect(() => {
    if (isAddingFile === node.path) {
      setIsOpen(true);
    }
  }, [isAddingFile, node.path]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleContextMenu = (e) => {
    e.preventDefault();
    // Always open the menu; the builder gates edit actions behind readOnly
    // while keeping read-only-safe actions like Compare available.
    onContextMenu(e, node);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isFolder) {
      toggleOpen();
    } else {
      onSelect(node.path);
    }
  };

  const handleNewFileKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedFileName = newFileName.trim();
      if (
        trimmedFileName &&
        !existingPaths.includes(`${node.path}/${trimmedFileName}`)
      ) {
        if (isAddingFile) {
          onAddFile(node.path, trimmedFileName);
        } else if (isRenamingFile) {
          onRenameFile(node.path, trimmedFileName);
        }
        setNewFileName("");
      }
    } else if (e.key === "Escape") {
      if (isAddingFile) {
        onCancelAddFile();
      } else if (isRenamingFile) {
        onCancelRenameFile();
      }
      setNewFileName("");
    }
  };

  const handleNewFileBlur = () => {
    if (isAddingFile) {
      onCancelAddFile();
    } else if (isRenamingFile) {
      onCancelRenameFile();
    }
    setNewFileName("");
  };

  const handleNewFileChange = (e) => {
    const value = e.target.value;
    if (/^$|^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value)) {
      setNewFileName(value);
    }
  };

  return (
    <div>
      {isRenamingFile === node.path && !readOnly ? (
        <div
          className="flex items-center py-1 px-2"
          style={{ paddingLeft: `${depth * 12}px` }}
        >
          <VscFile className="text-gray-400 w-4 h-4 mr-2" />
          <input
            ref={newFileInputRef}
            type="text"
            className="bg-gray-800 text-gray-300 text-xs p-1 w-full outline-none"
            value={newFileName}
            onChange={handleNewFileChange}
            onKeyDown={handleNewFileKeyDown}
            onBlur={handleNewFileBlur}
          />
        </div>
      ) : (
        <div
          className={`flex items-center py-1 px-2 cursor-pointer rounded-sm transition-colors duration-150 ease-in-out ${
            !isFolder && isSelected ? "bg-gray-700" : "hover:bg-gray-700"
          }`}
          style={{ paddingLeft: `${depth * 12}px` }}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
        >
          <div className="w-4 mr-1 flex-shrink-0">
            {isFolder &&
              (isOpen ? (
                <FaChevronDown className="text-gray-400 w-3 h-3" />
              ) : (
                <FaChevronRight className="text-gray-400 w-3 h-3" />
              ))}
          </div>
          <div className="mr-2 flex-shrink-0">
            {isFolder ? (
              isOpen ? (
                <VscFolderOpened className="text-blue-400 w-4 h-4" />
              ) : (
                <VscFolder className="text-blue-400 w-4 h-4" />
              )
            ) : (
              <VscFile className="text-gray-400 w-4 h-4" />
            )}
          </div>
          <span
            className={`text-xs truncate ${
              isFolder ? "text-gray-400" : "text-gray-300"
            }`}
          >
            {node.name}
          </span>
        </div>
      )}
      {isOpen && isFolder && (
        <div>
          {node.children.map((child, index) => (
            <PromptNode
              key={index}
              node={child}
              depth={depth + 1}
              onContextMenu={onContextMenu}
              initiallyExpanded={initiallyExpanded}
              selectedPath={selectedPath}
              onSelect={onSelect}
              onAddFile={onAddFile}
              isAddingFile={isAddingFile}
              onCancelAddFile={onCancelAddFile}
              isRenamingFile={isRenamingFile}
              onRenameFile={onRenameFile}
              onCancelRenameFile={onCancelRenameFile}
              existingPaths={existingPaths}
              onMovePrompt={onMovePrompt}
              readOnly={readOnly}
            />
          ))}
          {isAddingFile && node.path === isAddingFile && !readOnly && (
            <div
              className="flex items-center py-1 px-2"
              style={{ paddingLeft: `${(depth + 1) * 12}px` }}
            >
              <VscFile className="text-gray-400 w-4 h-4 mr-2" />
              <input
                ref={newFileInputRef}
                type="text"
                className="bg-gray-800 text-gray-300 text-xs p-1 w-full outline-none"
                value={newFileName}
                onChange={handleNewFileChange}
                onKeyDown={handleNewFileKeyDown}
                onBlur={handleNewFileBlur}
                placeholder="Enter name ..."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PromptTree = ({
  data,
  initiallyExpanded = true,
  selectedPath,
  onSelect,
  onDeletePrompt,
  onAddPrompt,
  onRenamePrompt,
  onMovePrompt,
  baseFolders,
  readOnly,
  selectedVersion,
  versions,
  onCompare,
  promptType,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [isAddingFile, setIsAddingFile] = useState(null);
  const [isRenamingFile, setIsRenamingFile] = useState(null);
  const [treeData, setTreeData] = useState([]);

  const handleSelect = useCallback(
    (path) => {
      onSelect(path);
    },
    [onSelect]
  );

  useEffect(() => {
    const handleEmulateTreeMenuClick = (e) => {
      handleSelect(e.detail.path);
    };
    window.addEventListener("emulateTreeMenuClick", handleEmulateTreeMenuClick);
    return () => {
      window.removeEventListener(
        "emulateTreeMenuClick",
        handleEmulateTreeMenuClick
      );
    };
  }, [handleSelect]);

  useEffect(() => {
    if (readOnly) {
      setContextMenu(null);
    }
  }, [readOnly]);

  const buildTree = useCallback(
    (paths) => {
      const root = { name: "root", children: [] };
      baseFolders.forEach((folder) => {
        const parts = folder.split("/");
        let currentNode = root;
        let currentPath = "";
        parts.forEach((part, index) => {
          currentPath += (index > 0 ? "/" : "") + part;
          let child = currentNode.children.find((c) => c.name === part);
          if (!child) {
            child = {
              name: part,
              path: currentPath,
              children: [],
              isOpen: false,
            };
            currentNode.children.push(child);
          }
          currentNode = child;
        });
      });
      paths.forEach((path) => {
        const parts = path.split("/");
        let currentNode = root;
        let currentPath = "";
        parts.forEach((part, index) => {
          currentPath += (index > 0 ? "/" : "") + part;
          let child = currentNode.children.find((c) => c.name === part);
          if (!child) {
            child = {
              name: part,
              path: currentPath,
              children: [],
              isOpen: false,
            };
            currentNode.children.push(child);
          }
          currentNode = child;
        });
      });
      return root.children;
    },
    [baseFolders]
  );

  useEffect(() => {
    setTreeData(buildTree(data));
  }, [data, buildTree]);

  const updateNodeInTree = useCallback((tree, updatedNode) => {
    return tree.map((node) => {
      if (node.path === updatedNode.path) {
        return { ...node, ...updatedNode };
      }
      if (node.children) {
        return {
          ...node,
          children: updateNodeInTree(node.children, updatedNode),
        };
      }
      return node;
    });
  }, []);

  const handleContextMenu = useCallback(
    (e, node) => {
      e.preventDefault();
      const depth = node.path.split("/").length - 1;
      const parentPath = node.path.split("/").slice(0, -1).join("/");
      const siblings = data.filter(
        (path) =>
          path.startsWith(parentPath) && path.split("/").length === depth + 1
      );
      const nodeIndex = siblings.indexOf(node.path);
      const menuItems = [];

      if (depth === 2 && node.path.startsWith("prompt/")) {
        const compareSubmenu = versions.map((version) => {
          const versionNumber = parseInt(version.split(" ")[0]);
          let label = version;
          if (
            versionNumber ===
            Math.max(...versions.map((v) => parseInt(v.split(" ")[0])))
          ) {
            label += " (Selected)";
          } else if (versionNumber === selectedVersion + 1) {
            label += " (Next)";
          } else if (versionNumber === selectedVersion - 1) {
            label += " (Previous)";
          }
          return {
            label,
            onClick: () => onCompare(node.path, versionNumber),
            disabled: versionNumber === selectedVersion,
          };
        });
        menuItems.push({
          label: "Compare",
          icon: FaExchangeAlt,
          submenu: compareSubmenu,
        });
      }

      if (!readOnly) {
        if (depth === 1 && node.path !== "prompt/primary") {
          menuItems.push({
            label: "Add new",
            icon: FaPlus,
            onClick: () => {
              setIsAddingFile(node.path);
              const updatedNode = { ...node, isOpen: true };
              setTreeData(updateNodeInTree(treeData, updatedNode));
            },
          });
        } else if (depth === 2) {
          if (
            parentPath.includes("pre-processing") ||
            parentPath.includes("post-processing")
          ) {
            if (nodeIndex > 0) {
              menuItems.push({
                label: "Move Up",
                icon: FaArrowUp,
                onClick: () => onMovePrompt(node.path, "up"),
              });
            }
            if (nodeIndex < siblings.length - 1) {
              menuItems.push({
                label: "Move Down",
                icon: FaArrowDown,
                onClick: () => onMovePrompt(node.path, "down"),
              });
            }
          }
          if (parentPath !== "prompt/primary") {
            menuItems.push(
              {
                label: "Rename",
                icon: FaPencilAlt,
                onClick: () => setIsRenamingFile(node.path),
              },
              {
                label: "Delete",
                icon: FaTrash,
                onClick: () => onDeletePrompt(node.path),
              }
            );
          }
        }
      }

      if (menuItems.length > 0) {
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          items: menuItems,
        });
      }
    },
    [
      data,
      onDeletePrompt,
      onMovePrompt,
      readOnly,
      versions,
      selectedVersion,
      onCompare,
      treeData,
      updateNodeInTree,
    ]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleAddFile = useCallback(
    (folderPath, fileName) => {
      const newPath = `${folderPath}/${fileName}`;
      const actualNewPath = onAddPrompt(newPath);
      setIsAddingFile(null);
      if (actualNewPath) {
        onSelect(actualNewPath);
      }
    },
    [onAddPrompt, onSelect]
  );

  const handleRenameFile = useCallback(
    (oldPath, newName) => {
      const pathParts = oldPath.split("/");
      pathParts.pop();
      const newPath = [...pathParts, newName].join("/");
      const actualNewPath = onRenamePrompt(oldPath, newPath);
      setIsRenamingFile(null);
      if (actualNewPath) {
        onSelect(actualNewPath);
      }
    },
    [onRenamePrompt, onSelect]
  );

  const handleCancelAddFile = useCallback(() => {
    setIsAddingFile(null);
  }, []);

  const handleCancelRenameFile = useCallback(() => {
    setIsRenamingFile(null);
  }, []);

  return (
    <>
      <div
        className="overflow-y-auto h-[100%] dark-slim-scrollbar"
        onClick={closeContextMenu}
        onContextMenu={(e) => e.preventDefault()}
      >
        {treeData.map((node, index) => (
          <PromptNode
            key={index}
            node={node}
            depth={0}
            onContextMenu={handleContextMenu}
            initiallyExpanded={initiallyExpanded}
            selectedPath={selectedPath}
            onSelect={handleSelect}
            onAddFile={handleAddFile}
            isAddingFile={isAddingFile}
            onCancelAddFile={handleCancelAddFile}
            isRenamingFile={isRenamingFile}
            onRenameFile={handleRenameFile}
            onCancelRenameFile={handleCancelRenameFile}
            existingPaths={data}
            onMovePrompt={onMovePrompt}
            readOnly={readOnly}
          />
        ))}
      </div>
      {contextMenu &&
        contextMenu.items.length > 0 &&
        ReactDOM.createPortal(
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenu.items}
            onClose={closeContextMenu}
          />,
          document.body
        )}
    </>
  );
};

export default PromptTree;
